import { useMatches, useRouteLoaderData } from "@remix-run/react";
import { createElement, memo, useMemo } from "react";

import { RawHtml } from "~/components/rawHtml/RawHtml";
import { HostContext } from "~/constants";
import { isMobileNative } from "~/utils/helpers";

import type { loader } from "~/root";

/**
 * Highly-memoized hook that returns global components.
 */
export function useGlobalComponents({
  hostContext,
}: {
  hostContext: HostContext;
}) {
  const data = useRouteLoaderData<typeof loader>("root");
  const matches = useMatches() as Array<{
    handle?: { includeGlobalUI?: boolean };
  }>;

  return useMemo(() => {
    const includeGlobalUI =
      !isMobileNative(hostContext) &&
      matches.every((m) => m.handle?.includeGlobalUI !== false);

    return {
      includeGlobalUI,
      GlobalMeta: memo(function GlobalMeta() {
        const viewport =
          data?.hostContext === HostContext.MobileNative
            ? "width=device-width, initial-scale=1.0, viewport-fit=cover, user-scalable=no, maximum-scale=1.0"
            : "width=device-width, initial-scale=1.0";

        return Object.entries({
          charset: "utf-8",
          viewport: viewport,
          "apple-itunes-app":
            "app-id=1523687027, affiliate-data=pt=118899364&ct=safari_smart_app_banner",
          "theme-color": "var(--color-white)",
        }).map(([k, v]) =>
          createElement("meta", { key: k, name: k, content: v }),
        );
      }),
      GlobalHeader: memo(function GlobalHeader() {
        if (!includeGlobalUI || !data?.header) return null;

        return createElement(RawHtml, {
          html: data?.header?.body,
          scripts: data?.header?.scripts,
        });
      }),
      GlobalFooter: memo(function GlobalFooter() {
        if (!includeGlobalUI || !data?.footer) return null;

        return createElement(RawHtml, {
          html: data?.footer?.body,
          scripts: data?.footer?.scripts,
        });
      }),
    };
  }, [data?.footer, data?.header, data?.hostContext, hostContext, matches]);
}
